import { ArrowBack, ArrowForward } from '@mui/icons-material';
import NoCrashIcon from '@mui/icons-material/NoCrash';
import EditIcon from '@mui/icons-material/Edit';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getService, getServicesByDate } from '../../api/servicesAPI';
import { getVehicle } from '../../api/vehiclesAPI';
import { formatDateTime } from '../../utils/dateUtils';
import NavBar from '../layout/NavBar';
import CustomerLookup from '../modals/CustomerLookup';
import ServiceModal from '../modals/ServiceModal';


function formatDateHeader(dateStr) {
  // parse as local date (avoid Date('YYYY-MM-DD') which can be treated as UTC)
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });
}

function toLocalInputDate(date = new Date()) {
  // produce YYYY-MM-DD for <input type="date"> using local date
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseLocalInputDate(dateStr) {
  // parse YYYY-MM-DD into a local Date at midnight
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function getServiceOwnersDisplay(service) {
  const listedOwners = service.vehicle?.list_owners_str?.trim();

  if (listedOwners) {
    return listedOwners;
  }

  const ownerNames = service.vehicle?.list_owners_object
    ?.map((owner) => owner?.name?.trim())
    .filter(Boolean)
    .join(', ');

  if (ownerNames) {
    return ownerNames;
  }

  const detailedServiceOwner =
    service.service_owner_details?.name?.trim() ??
    (
      service.service_owner &&
      typeof service.service_owner === 'object'
        ? service.service_owner.name?.trim()
        : ''
    );

  if (detailedServiceOwner) {
    return detailedServiceOwner;
  }

  if (
    typeof service.service_owner === 'string' &&
    service.service_owner.trim()
  ) {
    return service.service_owner.trim();
  }

  return 'n/a';
}

function getArrivalTypeDisplay(value) {
  switch (value) {
    case 'drop_off':
      return 'Drop off';
    case 'stay':
      return 'Stay';
    default:
      return 'Unspecified';
  }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(toLocalInputDate());
  const [showCompleted, setShowCompleted] = useState(true);
  const [services, setServices] = useState([]);

  // Notes modal state
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [noteService, setNoteService] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState(null);

   // Modal/edit state
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [modalServiceId, setModalServiceId] = useState(null);
  const [modalVehicleId, setModalVehicleId] = useState(null);
  const [modalOwners, setModalOwners] = useState([]);
  // Reusable fetch function so it can be called after edits/patches
  const fetchServices = useCallback(async (date = selectedDate) => {
    try {
      const response = await getServicesByDate(date);
      setServices(response.data);
    } catch (error) {
      setServices([]);
      console.error(error);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  let filteredServices = services;
  console.log('Fetched services:');
  console.log(services);
  if (!showCompleted) {
    filteredServices = filteredServices.filter(s => s.status !== 'Completed');
  }

  let completedCount = services.filter(s => s.status === 'Completed').length;

  const handleDateChange = (e) => setSelectedDate(e.target.value);
  const handlePrevDay = () => {
    const date = parseLocalInputDate(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(toLocalInputDate(date));
  };
  const handleNextDay = () => {
    const date = parseLocalInputDate(selectedDate);
    date.setDate(date.getDate() + 1);
    setSelectedDate(toLocalInputDate(date));
  };

  // Open edit modal for a particular service
  const warnIfCompletedService = (service, actionLabel) => {
    const status = String(service?.status ?? '').trim().toLowerCase();
    if (status === 'completed') {
      const confirmed = window.confirm(
        `This service has already been marked as completed.\n\n` +
        `Editing will delete the signature.\n\n` +
        `Are you sure you want to ${actionLabel} it?`
      );
      return !confirmed;
    }
    return false;
  };

  const handleEditService = async (service) => {
    if (warnIfCompletedService(service, 'edit')) {
      return;
    }

    try {
      const serviceResponse = await getService(service.id);
      const fullService = serviceResponse.data;
      const resolvedVehicleId =
        fullService.vehicle?.id ??
        fullService.vehicle_id ??
        service.vehicle?.id ??
        null;

      setModalServiceId(fullService.id ?? service.id);
      setModalVehicleId(resolvedVehicleId);

      if (resolvedVehicleId) {
        const vehicleResponse = await getVehicle(
          resolvedVehicleId
        );
        setModalOwners(
          vehicleResponse.data?.list_owners_object ??
          []
        );
      } else {
        setModalOwners([]);
      }
    } catch (error) {
      setModalServiceId(service.id);
      setModalVehicleId(service.vehicle?.id ?? null);
      setModalOwners(
        service.vehicle?.list_owners_object ?? []
      );
      console.error(error);
    } finally {
      setIsServiceModalOpen(true);
    }
  };

  // Open notes viewer; fetch full service to ensure notes are current
  const handleViewNotes = async (service) => {
    setIsNotesOpen(true);
    setNotesLoading(true);
    setNotesError(null);
    try {
      const response = await getService(service.id);
      setNoteService(response.data);
    } catch (error) {
      setNotesError('Unable to load notes.');
      setNoteService(service);
      console.error(error);
    } finally {
      setNotesLoading(false);
    }
  };

  const handleCloseNotes = () => {
    setIsNotesOpen(false);
    setNoteService(null);
    setNotesError(null);
  };

  const handleOpenCompleteService = async (service) => {
    if (warnIfCompletedService(service, 'complete')) {
      return;
    }

    try {
      const response = await getService(service.id);
      navigate('/services/' + response.data.id, {
        state: {
          openCompleteDialog: true,
          returnTo: {
            pathname: '/',
            state: {
              selectedDate,
              showCompleted,
            },
          },
        },
      });
    } catch (error) {
      navigate('/services/' + service.id, {
        state: {
          openCompleteDialog: true,
          returnTo: {
            pathname: '/',
            state: {
              selectedDate,
              showCompleted,
            },
          },
        },
      });
      console.error(error);
    }
  };

  const handleModalClose = async () => {
    setIsServiceModalOpen(false);
    setModalServiceId(null);
    setModalVehicleId(null);
    setModalOwners([]);
    // Refresh services after closing modal to pick up edits
    await fetchServices();
  };

  return (
    <Box>
      <Typography variant='h2' align='center'>Dashboard</Typography>
      <NavBar active='Dashboard' />
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ width: '100%', mt: 3, px: 2, boxSizing: 'border-box' }}>
        <CustomerLookup />
      </Box>
      <Box sx={{ width: '100%', px: 2, mt: 2, boxSizing: 'border-box', display: 'flex', justifyContent: 'center' }}>
        <Paper sx={{ p: 3, width: 'min(calc(100vw - 32px), 1600px)', maxWidth: '100%', boxSizing: 'border-box' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2,
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <IconButton onClick={handlePrevDay}><ArrowBack /></IconButton>
              <TextField
                type='date'
                value={selectedDate}
                onChange={handleDateChange}
                sx={{ mx: 1 }}
                size='small'
              />
              <IconButton onClick={handleNextDay}><ArrowForward /></IconButton>
              <Typography
                variant='h6'
                sx={{
                  ml: { xs: 0, md: 2 },
                  whiteSpace: 'nowrap',
                  flexGrow: 1,
                }}
              >
                Services for {formatDateHeader(selectedDate)}
              </Typography>
              <Button
                variant='contained'
                onClick={() => setShowCompleted(sc => !sc)}
                sx={{ backgroundColor: 'grey.500', ml: { xs: 0, md: 'auto' }, whiteSpace: 'nowrap' }}
              >
                {(showCompleted ? 'Hide Completed' : 'Show Completed') + ` (${completedCount})`}
              </Button>
            </Box>
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Appointment</TableCell>
                    <TableCell>Vehicle</TableCell>
                    <TableCell>Service Description</TableCell>
                    <TableCell>Estimated Time</TableCell>
                    <TableCell>Drop Off / Stay</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredServices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align='center'>No services scheduled for this day.</TableCell>
                    </TableRow>
                  ) : (
                    filteredServices.map((service) => {
                      const vehicleId =
                        service.vehicle?.id ??
                        service.vehicle_id ??
                        null;
                      const vehicleDescription =
                        service.vehicle?.short_description ??
                        'n/a';

                      return (
                        <TableRow key={service.id}>
                          <TableCell size='small'>{formatDateTime(service.datetime)}</TableCell>
                          <TableCell size='small'>
                            {vehicleId ? (
                              <Link to={'/vehicles/' + vehicleId}>{vehicleDescription}</Link>
                            ) : (
                              vehicleDescription
                            )}
                          </TableCell>
                          <TableCell size='small' sx={{ maxWidth: 260, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                            {service.service_items_str || '—'}
                          </TableCell>
                          <TableCell size='small'>{service.estimated_time}</TableCell>
                          <TableCell size='small'>{getArrivalTypeDisplay(service.arrival_type)}</TableCell>
                          <TableCell size='small'>{getServiceOwnersDisplay(service)}</TableCell>
                          <TableCell size='small'>{service.status}</TableCell>
                          <TableCell size='small'>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', width: 'fit-content' }}>
                              <Tooltip title='Complete Service'>
                                <IconButton onClick={() => handleOpenCompleteService(service)}>
                                  <NoCrashIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Service">
                                <IconButton onClick={() => handleEditService(service)}>
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Notes">
                                <IconButton onClick={() => handleViewNotes(service)}>
                                  <StickyNote2Icon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Service">
                                <Link
                                  to={'/services/' + service.id}
                                  state={{
                                    returnTo: {
                                      pathname: '/',
                                      state: {
                                        selectedDate,
                                        showCompleted,
                                      },
                                    },
                                  }}
                                >
                                  <IconButton>
                                    <VisibilityIcon />
                                  </IconButton>
                                </Link>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
        </Paper>
      </Box>
      {/* Service edit modal */}
      <ServiceModal
        isOpen={isServiceModalOpen}
        onClose={handleModalClose}
        vehicleId={modalVehicleId}
        owners={modalOwners}
        estimate={null}
        serviceId={modalServiceId}
        refreshFunction={fetchServices}
      />

      {/* Notes modal */}
      <Dialog open={isNotesOpen} onClose={handleCloseNotes} maxWidth="sm" fullWidth>
        <DialogTitle>Service Notes</DialogTitle>
        <DialogContent dividers>
          {notesLoading && <Typography>Loading…</Typography>}
          {notesError && <Typography color="error">{notesError}</Typography>}
          {!notesLoading && noteService && (
            <Box>
              <Typography variant='subtitle1'>Customer Notes</Typography>
              <Paper variant='outlined' sx={{ p: 1, mb: 2, whiteSpace: 'pre-wrap', minHeight: 64 }}>
                {noteService.customer_notes?.trim() || 'No customer notes.'}
              </Paper>
              <Divider sx={{ mb: 2 }} />
              <Typography variant='subtitle1'>Internal Notes</Typography>
              <Paper variant='outlined' sx={{ p: 1, whiteSpace: 'pre-wrap', minHeight: 64 }}>
                {noteService.internal_notes?.trim() || 'No internal notes.'}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNotes}>Close</Button>
          {noteService && (
            <Button
              variant='contained'
              onClick={() => {
                handleCloseNotes();
                handleEditService(noteService);
              }}
            >
              Edit Service
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
